import { useEffect, useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  acceptAgreement,
  getCurrentAgreements,
  getPendingAgreements,
  type Agreement,
} from '../lib/api.js';
import { Note, Problem, button } from '../components/form.js';

/**
 * What this account has agreed to, and what it still owes.
 *
 * ── Both endpoints, not just the pending one ─────────────────────────
 *
 * `/agreements/pending` answers "what do I owe" and `/agreements/current`
 * answers "what applies to me". Showing only the first makes the screen empty
 * for the common case — somebody who has accepted everything — which reads as
 * "there are no agreements" rather than "you are up to date".
 *
 * ── The session field is NOT the source ──────────────────────────────
 *
 * `user.pendingAgreements` rides along on every session read and is tempting for
 * exactly that reason. It is a CACHE: it cannot filter retired agreements or
 * ones out of scope for this account's role, and the auth service documents it
 * as a hint whose only honest use is "something is probably outstanding". This
 * screen asks the endpoint.
 *
 * ── Waiting on a guardian is not waiting on you (auth#615) ───────────
 *
 * For a member under 18, an agreement counts only once they AND a guardian in
 * their Family Group have accepted it, so auth keeps it pending until both
 * halves exist, and says which (`halves`). One they have accepted themselves is
 * shown as waiting for a guardian, with no Accept button: offering it again
 * asked for an acceptance already on record, and "Not yet accepted" contradicted
 * the banner above it. The split is the one `App.tsx` makes for the gate.
 */
const waitingForGuardian = (a: Agreement) => a.halves?.self === true && a.halves.guardian === false;
export function Agreements() {
  const [pending, setPending] = useState<Agreement[]>([]);
  const [current, setCurrent] = useState<Agreement[]>([]);
  /**
   * "Have the requests come back" is its own fact, tracked separately.
   *
   * It used to be inferred from the data being non-null, which is the same
   * conflation `App.tsx` avoids between "no session" and "session lookup
   * failed": any value that arrives falsy becomes indistinguishable from not
   * having arrived, and the screen waits for something that already happened.
   */
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    try {
      const [p, c] = await Promise.all([getPendingAgreements(), getCurrentAgreements()]);
      /**
       * `?? []` is NOT defensive noise — it is what stops a shape mismatch
       * looking like a slow network.
       *
       * This read `c.agreements` while the endpoint answers `{ current }`, so a
       * perfectly good 200 set state to `undefined`, the guard below saw a
       * falsy value, and the screen said "Loading…" forever. No console error,
       * no failed request, nothing to search for.
       *
       * Defaulting means a wrong key now renders an EMPTY list — visibly wrong,
       * and wrong in a way that points at the data rather than at the network.
       */
      setPending(p.pending ?? []);
      setCurrent(c.current ?? []);
      setLoaded(true);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your agreements.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function accept(a: Agreement) {
    setBusy(a.agreementSlug);
    try {
      await acceptAgreement(a.agreementVersionId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That could not be recorded.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !loaded) return <Problem>{error}</Problem>;
  if (!loaded) return <Note>Loading…</Note>;

  const mine = pending.filter((a) => !waitingForGuardian(a));
  const theirs = pending.filter(waitingForGuardian);

  return (
    <section style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: '1.15rem', marginBottom: '1rem' }}>Agreements</h2>
      {error && <Problem>{error}</Problem>}

      {mine.length > 0 ? (
        <>
          <h3 style={{ fontSize: '1rem', margin: '0 0 .5rem' }}>Waiting for you</h3>
          <ul style={list}>
            {mine.map((a) => (
              <li key={a.agreementSlug} style={row}>
                <div>
                  <div>{a.name}</div>
                  <Note>{a.version ? `Version ${a.version}` : 'Current version'}</Note>
                </div>
                <button
                  type="button"
                  disabled={busy === a.agreementSlug}
                  onClick={() => void accept(a)}
                  style={{ ...button, marginTop: 0, whiteSpace: 'nowrap' }}
                >
                  {busy === a.agreementSlug ? 'Recording…' : 'Accept'}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : theirs.length === 0 ? (
        /*
          Said positively. An empty list is ambiguous — it could mean "nothing
          applies to you", which is a different and less reassuring thing than
          "you are up to date".
        */
        <Note tone="ok">You are up to date. Nothing is waiting for you.</Note>
      ) : null}

      {theirs.length > 0 && (
        <>
          <h3 style={{ fontSize: '1rem', margin: mine.length > 0 ? '2rem 0 .5rem' : '0 0 .5rem' }}>
            Waiting for a parent or guardian
          </h3>
          <ul style={list}>
            {theirs.map((a) => (
              <li key={a.agreementSlug} style={row}>
                <div>
                  <div>{a.name}</div>
                  <Note>{a.version ? `Version ${a.version}` : 'Current version'}</Note>
                </div>
                <Note>You accepted. A parent or guardian needs to accept too.</Note>
              </li>
            ))}
          </ul>
        </>
      )}

      <h3 style={{ fontSize: '1rem', margin: '2rem 0 .5rem' }}>In force</h3>
      {current.length === 0 ? (
        <Note>Nothing applies to your account yet.</Note>
      ) : (
        <ul style={list}>
          {current.map((a) => (
            <li key={a.agreementSlug} style={row}>
              <div>
                <div>{a.name}</div>
                <Note>{a.version ? `Version ${a.version}` : ''}</Note>
              </div>
              <Note tone={a.acceptedAt ? 'ok' : 'muted'}>{inForceStatus(a)}</Note>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const list: React.CSSProperties = { listStyle: 'none', padding: 0, margin: 0 };
const row: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  padding: '.85rem 0',
  borderTop: `1px solid ${C.inputBorder}`,
};

/** The line beside an in-force agreement. With `halves`, both a member under 18 and a guardian must accept. */
function inForceStatus(a: Agreement): string {
  if (a.acceptedAt) return `Accepted ${new Date(a.acceptedAt).toLocaleDateString()}`;
  if (a.halves?.self) return 'You accepted. Waiting for a parent or guardian';
  if (a.halves?.guardian) return 'Your guardian accepted. Waiting for you';
  return 'Not yet accepted';
}
